import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  CountriesDropdownProps,
  PhoneTextFieldProps,
} from "./PhoneTextField.types";
import styles from "./PhoneTextField.module.scss";
import { TextField, Typography } from "../index";
import { IconContext } from "react-icons";
import { CISearch } from "@dua.design/icons";
import { countryDialCode } from "../../common/countryDialCode";

const PhonePhoneTextField = React.forwardRef<
  HTMLInputElement & HTMLTextAreaElement,
  PhoneTextFieldProps
>(
  (
    {
      label,
      required,
      helperText,
      hint,
      error,
      prefix,
      countryPlaceholder,
      onCountryChange,
      link,
      onLinkClick,
      initialValue,
      ...rest
    },
    ref
  ) => {
    const [inputState, setInputState] = useState<
      "normal" | "hover" | "focus" | "disabled" | "readOnly" | "error"
    >("normal");
    const [countriesDropdown, setCountriesDropdown] =
      useState<CountriesDropdownProps>({
        toggle: false,
        value: "",
      });
    const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
    const [isDefaultValue, setIsDefaultValue] = useState(true);
    useImperativeHandle(ref, () => inputRef.current);

    useEffect(() => {
      if (rest.disabled) {
        setInputState("disabled");
      } else if (rest.readOnly) {
        setInputState("readOnly");
      } else if (error) {
        setInputState("error");
      } else {
        setInputState("normal");
      }
    }, [rest.disabled, rest.readOnly, error]);

    const handleInputFocus = (e: MouseEvent) => {
      if (e.type === "focus") {
        setInputState((prevState) => {
          if (prevState === "normal" || prevState === "hover") {
            return "focus";
          }
          return prevState;
        });
      } else if (e.type === "blur") {
        setInputState((prevState) => {
          if (prevState === "hover" || prevState === "focus") {
            return "normal";
          }
          return prevState;
        });
      } else if (e.type === "mouseenter") {
        setInputState((prevState) => {
          if (prevState === "normal") {
            return "hover";
          }
          return prevState;
        });
      } else if (e.type === "mouseleave") {
        setInputState((prevState) => {
          if (prevState === "hover") {
            return "normal";
          }
          return prevState;
        });
      }
    };

    useEffect(() => {
      setIsDefaultValue(inputRef.current.value === initialValue);
      ["mouseenter", "mouseleave", "focus", "blur"].forEach((event) => {
        inputRef.current.addEventListener(event, handleInputFocus);
      });

      return () => {
        ["mouseenter", "mouseleave", "focus", "blur"].forEach((event) => {
          inputRef.current?.removeEventListener(event, handleInputFocus);
        });
      };
    }, []);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsDefaultValue(e.target.value === initialValue);
      rest.onChange && rest.onChange(e);
    };

    return (
      <div>
        <div className={styles.PhoneTextField} data-testid="PhoneTextField">
          {(label || link || required || hint) && (
            <div className={styles.labelContainer}>
              <Typography
                token={100}
                weight={"normal"}
                variant={"label"}
                className={styles.label}
              >
                {label} {required && <span className={styles.required}>*</span>}
              </Typography>
              <div className={styles.rightSide}>
                {link ? (
                  <button
                    className={styles.noStyleButton}
                    onClick={onLinkClick}
                  >
                    <Typography
                      token={100}
                      weight={"normal"}
                      variant={"p"}
                      className={styles.link}
                    >
                      {link}
                    </Typography>
                  </button>
                ) : (
                  hint && (
                    <Typography
                      token={100}
                      weight={"normal"}
                      variant={"p"}
                      className={styles.hint}
                    >
                      {hint}
                    </Typography>
                  )
                )}
              </div>
            </div>
          )}
          <IconContext.Provider value={{ size: "16px" }}>
            <div
              className={`${styles.inputContainer} ${
                countriesDropdown.toggle ? styles.activeInputContainer : ""
              }`}
              data-state={inputState}
            >
              {prefix && (
                <div className={styles.prefix}>
                  <button
                    type="button"
                    onClick={() =>
                      setCountriesDropdown({
                        ...countriesDropdown,
                        toggle: !countriesDropdown.toggle,
                      })
                    }
                  >
                    <Typography variant={"p"} token={100} weight={"normal"}>
                      {prefix}
                    </Typography>
                  </button>
                </div>
              )}
              <input
                ref={inputRef}
                {...rest}
                onChange={(e) => {
                  const regex = /^[0-9]+$/;
                  const inputValue = e.target.value;

                  if (!inputValue.length || regex.test(inputValue)) {
                    onChange(e);
                    setInputState('focus');
                  } else {
                    setInputState('error');
                  }
                }}
                className={[
                  styles.input,
                  isDefaultValue ? styles.defaultValue : null,
                  rest.className,
                ].join(" ")}
              />
            </div>
          </IconContext.Provider>
          {helperText && (
            <div className={styles.helperText} data-state={inputState}>
              <Typography variant={"p"} token={100} weight={"normal"}>
                {helperText}
              </Typography>
            </div>
          )}
        </div>
        {countriesDropdown.toggle ? (
          <div className={styles.PhoneTextFieldDropdown}>
            <div>
              <TextField
                value={countriesDropdown.value}
                placeholder={countryPlaceholder}
                onChange={(e) =>
                  setCountriesDropdown({
                    ...countriesDropdown,
                    value: e.target.value,
                  })
                }
                iconBefore={<CISearch />}
              />
              <ul className={styles.PhoneTextFieldDropdownItems}>
                {countryDialCode
                  .filter(
                    (item) =>
                      item.name
                        .toLowerCase()
                        .startsWith(countriesDropdown.value.toLowerCase()) ||
                      item.dial_code.startsWith(
                        countriesDropdown.value.toLowerCase()
                      )
                  )
                  .map((item) => (
                    <li key={`${item.dial_code}-${item.name}`}>
                      <button
                        type="button"
                        onClick={() => {
                          setCountriesDropdown({
                            ...countriesDropdown,
                            toggle: false,
                            value: "",
                          });
                          onCountryChange(item.dial_code);
                        }}
                      >
                        <Typography
                          variant={"span"}
                          token={100}
                          weight={"normal"}
                        >
                          {item.dial_code.replace("+", "")}
                        </Typography>
                        <Typography
                          variant={"span"}
                          token={100}
                          weight={"medium"}
                        >
                          {item.name}
                        </Typography>
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
);
PhonePhoneTextField.displayName = "PhoneTextField";

export default PhonePhoneTextField;
